import React, {useCallback, useEffect, useRef, useState} from 'react';
import {formatDateTime} from '../utils/dateTime';
import {useNavigate} from 'react-router-dom';
import createAxiosInstance from '../support/AxiosSupport';
import usePasteImages, {IMAGE_ACCEPT, imageRejectReason} from '../hooks/usePasteImages';
import {useToast} from '../context/ToastContext';
import {SparklesIcon} from '../components/icons';
import AddClothingItemModal from '../components/AddClothingItemModal';
import {useDocumentTitle} from '../hooks/useDocumentTitle';

const InlineSpinner: React.FC = () => (
    <svg className="animate-spin w-4 h-4" viewBox="0 0 24 24" fill="none">
        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/>
        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z"/>
    </svg>
);

interface Scores {
    compatibility: number; fit: number; colors: number; condition: number;
    relevance: number; ageAppropriateness: number; scenarioFit: number;
    weatherFit: number; personalStyleFit: number;
}

interface ScenarioItem {scenario: string; score: number; verdict: string}
interface Issue {type: string; severity: 'low' | 'medium' | 'high'; item: string; description: string}
interface Recommendation {priority: 'high' | 'medium' | 'low'; action: string; target: string; description: string}
interface RetirementCandidate {description: string; reason: string; confidence: number}

interface AiPayload {
    scores?: Scores; scenarioAssessment?: ScenarioItem[];
    detectedIssues?: Issue[]; recommendations?: Recommendation[];
    retirementCandidates?: RetirementCandidate[];
}

interface GarmentCandidate {
    itemId: string; name: string; brandName: string | null;
    categoryName: string | null; primaryPhotoUrl: string | null; matchPercent: number;
}

interface DetectedGarment {
    slot: string | null; label: string | null; description: string | null;
    suggestedCategoryCode: string | null; suggestedCategoryId: string | null;
    colorNames: string[] | null; colorIds: string[] | null;
    confidence: number | null; candidates: GarmentCandidate[] | null;
}

interface EvaluationResult {
    id: string; imageUrl: string | null; comment: string | null;
    scenario: string | null; overallScore: number | null; verdict: string | null;
    summary: string | null; aiResponse: AiPayload | null; outfitName?: string | null;
    detectedGarments?: DetectedGarment[] | null; createdAt: string;
}

const SCENARIO_LABELS: Record<string, string> = {
    work: 'Работа', walk: 'Прогулка', restaurant: 'Ресторан', meeting: 'Встреча',
    travel: 'Поездка', sport: 'Спорт', home: 'Дача/Дом', date: 'Свидание',
    formal: 'Мероприятие', shopping: 'Шопинг',
};

const VERDICT_LABELS: Record<string, {label: string; color: string}> = {
    wear: {label: 'Можно носить', color: 'text-green-600 dark:text-green-400'},
    wear_with_changes: {label: 'Можно с правками', color: 'text-yellow-600 dark:text-yellow-400'},
    change_outfit: {label: 'Лучше переодеться', color: 'text-red-600 dark:text-red-400'},
    not_for_scenario: {label: 'Не для этого случая', color: 'text-orange-600 dark:text-orange-400'},
    retire_item: {label: 'Вещь кандидат на выбытие', color: 'text-red-700 dark:text-red-500'},
    replace_item: {label: 'Заменить вещь', color: 'text-red-600 dark:text-red-400'},
};

const SLOT_LABELS: Record<string, string> = {
    BASE: 'Верх (база)', MID: 'Средний слой', OUTER: 'Верхняя одежда',
    BOTTOM: 'Низ', FOOTWEAR: 'Обувь', ACCESSORY: 'Аксессуар',
};

const SEVERITY_COLORS: Record<string, string> = {
    low: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-500/20 dark:text-yellow-300',
    medium: 'bg-orange-100 text-orange-800 dark:bg-orange-500/20 dark:text-orange-300',
    high: 'bg-red-100 text-red-800 dark:bg-red-500/20 dark:text-red-300',
};

const PRIORITY_BORDER: Record<string, string> = {
    high: 'border-red-500',
    medium: 'border-yellow-400',
    low: 'border-gray-400 dark:border-gray-600',
};

const SCORE_LABELS: [keyof Scores, string][] = [
    ['compatibility', 'Сочетаемость'], ['fit', 'Посадка'], ['colors', 'Цвета'],
    ['condition', 'Состояние'], ['relevance', 'Актуальность'],
    ['ageAppropriateness', 'Возраст'], ['scenarioFit', 'Сценарий'],
    ['weatherFit', 'Погода'], ['personalStyleFit', 'Стиль'],
];

function PhotoLightbox({src, onClose}: {src: string; onClose: () => void}) {
    useEffect(() => {
        const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
        document.addEventListener('keydown', onKey);
        return () => document.removeEventListener('keydown', onKey);
    }, [onClose]);
    return (
        <div
            className="fw-overlay fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm"
            onClick={onClose}
        >
            <img
                src={src} alt="образ"
                className="max-h-[90vh] max-w-[90vw] rounded-2xl shadow-2xl object-contain"
                onClick={e => e.stopPropagation()}
            />
            <button
                onClick={onClose}
                className="absolute top-4 right-4 w-10 h-10 flex items-center justify-center rounded-full bg-white/10 hover:bg-white/20 text-white text-2xl transition"
            >×</button>
        </div>
    );
}

function SectionTitle({children}: {children: React.ReactNode}) {
    return (
        <h3 className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-widest mb-3">
            {children}
        </h3>
    );
}

function ScoreBar({label, value}: {label: string; value: number}) {
    const pct = Math.round(value * 10);
    const barColor = value >= 7 ? 'bg-green-500' : value >= 5 ? 'bg-yellow-500' : 'bg-red-500';
    const numColor = value >= 7 ? 'text-green-500 dark:text-green-400'
        : value >= 5 ? 'text-yellow-500 dark:text-yellow-400'
        : 'text-red-500 dark:text-red-400';
    return (
        <div className="flex items-center gap-3">
            <span className="w-28 text-xs text-gray-500 dark:text-gray-400 shrink-0">{label}</span>
            <div className="flex-1 h-2 rounded-full bg-gray-200 dark:bg-gray-700">
                <div className={`h-2 rounded-full ${barColor}`} style={{width: `${pct}%`}}/>
            </div>
            <span className={`w-10 text-right text-xs font-bold ${numColor}`}>{value}/10</span>
        </div>
    );
}

interface CreatedItem {id: string; name?: string; primaryPhotoUrl?: string | null}

function matchChip(pct: number): string {
    return pct >= 80
        ? 'bg-green-100 text-green-800 dark:bg-green-500/20 dark:text-green-300'
        : pct >= 50
        ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-500/20 dark:text-yellow-300'
        : 'bg-gray-100 text-gray-600 dark:bg-gray-500/20 dark:text-gray-300';
}

function imageUrlFromKey(key: string | null | undefined): string | null {
    return key ? `/api/clothing/images/${key}` : null;
}

/** Блок «Распознанные вещи» (CTHS-200): состав образа + выбор/создание вещей + сохранение. */
function CompositionSection({result}: {result: EvaluationResult}) {
    const navigate = useNavigate();
    const garments = result.detectedGarments ?? [];

    const [selected, setSelected] = useState<Record<number, string>>(() => {
        const init: Record<number, string> = {};
        garments.forEach((g, i) => {
            const top = (g.candidates ?? [])[0];
            if (top && top.matchPercent >= 70) init[i] = top.itemId;
        });
        return init;
    });
    const [created, setCreated] = useState<Record<number, GarmentCandidate>>({});
    const [modalIdx, setModalIdx] = useState<number | null>(null);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState<string | null>(null);

    if (garments.length === 0) return null;

    const select = (i: number, itemId: string) =>
        setSelected(p => (p[i] === itemId ? (() => { const n = {...p}; delete n[i]; return n; })() : {...p, [i]: itemId}));

    const handleCreated = (idx: number, raw: CreatedItem) => {
        const id = raw?.id;
        if (!id) return;
        const g = garments[idx];
        setCreated(p => ({...p, [idx]: {
            itemId: id, name: raw.name ?? g.label ?? 'Новая вещь', brandName: null,
            categoryName: null, primaryPhotoUrl: raw.primaryPhotoUrl ?? null, matchPercent: 100,
        }}));
        setSelected(p => ({...p, [idx]: id}));
        setModalIdx(null);
    };

    const chosenCount = Object.values(selected).filter(Boolean).length;

    const save = async () => {
        if (chosenCount === 0) return;
        setSaving(true);
        setError(null);
        try {
            const api = await createAxiosInstance();
            const items = Object.entries(selected)
                .filter(([, itemId]) => itemId)
                .map(([i, itemId]) => ({itemId, slot: garments[Number(i)].slot ?? undefined}));
            const res = await api.post<{id: string}>(`/outfit-evaluation/${result.id}/save-as-outfit`, {items});
            navigate(`/outfits/saved/${res.data.id}`);
        } catch (err: unknown) {
            const e = err as {response?: {data?: {message?: string; error?: string; description?: string}}};
            setError(e?.response?.data?.description || e?.response?.data?.message
                || 'Не удалось сохранить образ. Попробуйте ещё раз.');
        } finally {
            setSaving(false);
        }
    };

    const modalGarment = modalIdx !== null ? garments[modalIdx] : null;

    return (
        <div className="p-6 border-b border-gray-100 dark:border-white/10">
            <div className="flex items-center justify-between gap-3 mb-4 flex-wrap">
                <div>
                    <SectionTitle>Распознанные вещи</SectionTitle>
                    {result.outfitName && (
                        <div className="text-base font-semibold text-gray-900 dark:text-white -mt-1">
                            {result.outfitName}
                        </div>
                    )}
                </div>
                <div className="flex items-center gap-3">
                    {error && <span className="text-sm text-red-500 dark:text-red-400">{error}</span>}
                    <button
                        onClick={save}
                        disabled={chosenCount === 0 || saving}
                        className="px-4 py-2 rounded-xl bg-brand-500 hover:bg-brand-600 disabled:opacity-40 text-white text-sm font-semibold transition"
                    >
                        {saving ? 'Сохраняю…' : `Сохранить как образ${chosenCount ? ` (${chosenCount})` : ''}`}
                    </button>
                </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                {garments.map((g, i) => {
                    const cands = [...(g.candidates ?? [])];
                    const createdCand = created[i];
                    if (createdCand && !cands.some(c => c.itemId === createdCand.itemId)) {
                        cands.unshift(createdCand);
                    }
                    return (
                        <div key={i} className="rounded-xl border border-gray-100 dark:border-white/10 bg-gray-50 dark:bg-ink-700/40 p-3">
                            <div className="text-sm font-semibold text-gray-900 dark:text-white mb-2 truncate" title={g.label ?? ''}>
                                {g.label || SLOT_LABELS[g.slot ?? ''] || 'Вещь'}
                            </div>
                            <div className="space-y-2">
                                {cands.length === 0 && (
                                    <p className="text-xs text-gray-400 dark:text-gray-500">В гардеробе не нашлось похожих вещей</p>
                                )}
                                {cands.map(c => {
                                    const isSel = selected[i] === c.itemId;
                                    const photo = imageUrlFromKey(c.primaryPhotoUrl);
                                    return (
                                        <button
                                            key={c.itemId}
                                            onClick={() => select(i, c.itemId)}
                                            className={`w-full flex items-center gap-2 rounded-lg p-2 text-left transition border ${
                                                isSel
                                                    ? 'border-brand-500 ring-1 ring-brand-500 bg-white dark:bg-ink-800'
                                                    : 'border-transparent hover:border-gray-200 dark:hover:border-white/10 bg-white dark:bg-ink-800'
                                            }`}
                                        >
                                            <div className="w-12 h-12 rounded-md overflow-hidden bg-gray-100 dark:bg-ink-700 shrink-0 flex items-center justify-center">
                                                {photo
                                                    ? <img src={photo} alt={c.name} className="w-full h-full object-cover"/>
                                                    : <span className="text-gray-300 dark:text-gray-600 text-xs">нет фото</span>}
                                            </div>
                                            <div className="min-w-0 flex-1">
                                                <div className="text-sm text-gray-900 dark:text-white truncate">{c.name}</div>
                                                <span className={`inline-block mt-0.5 rounded px-1.5 py-0.5 text-[11px] font-semibold ${matchChip(c.matchPercent)}`}>
                                                    {c.matchPercent}%
                                                </span>
                                            </div>
                                            <span className={`w-4 h-4 rounded-full border shrink-0 ${
                                                isSel ? 'bg-brand-500 border-brand-500' : 'border-gray-300 dark:border-gray-600'
                                            }`}/>
                                        </button>
                                    );
                                })}
                                <button
                                    onClick={() => setModalIdx(i)}
                                    className="w-full text-xs font-medium text-brand-600 dark:text-brand-400 hover:underline py-1.5 text-center"
                                >
                                    Этой вещи нет
                                </button>
                            </div>
                        </div>
                    );
                })}
            </div>

            {modalGarment && (
                <AddClothingItemModal
                    isOpen={modalIdx !== null}
                    onClose={() => setModalIdx(null)}
                    onSuccess={() => {}}
                    onCreated={(item) => handleCreated(modalIdx!, item as CreatedItem)}
                    prefill={{
                        name: modalGarment.label ?? '',
                        description: modalGarment.description ?? '',
                        categoryId: modalGarment.suggestedCategoryId ?? null,
                        colorIds: modalGarment.colorIds ?? null,
                    }}
                />
            )}
        </div>
    );
}

function ResultCard({result, onClose}: {result: EvaluationResult; onClose: () => void}) {
    const ai = result.aiResponse;
    const verdictInfo = VERDICT_LABELS[result.verdict ?? ''] ?? {label: result.verdict ?? '', color: 'text-gray-500'};
    const score = result.overallScore ?? 0;
    const scoreColor = score >= 7 ? 'text-green-500 dark:text-green-400'
        : score >= 5 ? 'text-yellow-500 dark:text-yellow-400'
        : 'text-red-500 dark:text-red-400';
    const [lightbox, setLightbox] = useState(false);

    return (
        <>
        {lightbox && result.imageUrl && <PhotoLightbox src={result.imageUrl} onClose={() => setLightbox(false)}/>}
        <div className="bg-white dark:bg-ink-800 rounded-2xl shadow-xl overflow-hidden border border-gray-100 dark:border-white/10">

            {/* ── Hero: фото + оценка + резюме ── */}
            <div className="flex gap-8 p-6 border-b border-gray-100 dark:border-white/10">
                {result.imageUrl && (
                    <button
                        onClick={() => setLightbox(true)}
                        className="shrink-0 rounded-xl overflow-hidden ring-1 ring-black/10 dark:ring-white/10 hover:ring-2 hover:ring-brand-500 transition focus:outline-none"
                        title="Открыть фото"
                    >
                        <img
                            src={result.imageUrl} alt="образ"
                            className="w-52 h-72 object-cover"
                        />
                    </button>
                )}
                <div className="flex-1 min-w-0 flex flex-col justify-between">
                    <div>
                        <div className="flex items-start justify-between gap-3">
                            <div className="flex items-baseline gap-3 flex-wrap">
                                <span className={`text-7xl font-black tracking-tight leading-none ${scoreColor}`}>
                                    {result.overallScore?.toFixed(1)}
                                </span>
                                <span className="text-2xl text-gray-400 dark:text-gray-500 self-end mb-1">/10</span>
                                <span className={`text-lg font-semibold self-end mb-1 ${verdictInfo.color}`}>
                                    {verdictInfo.label}
                                </span>
                            </div>
                            <button
                                onClick={onClose}
                                className="shrink-0 w-8 h-8 flex items-center justify-center rounded-lg text-gray-400 hover:text-gray-700 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-ink-700 transition text-xl"
                            >×</button>
                        </div>
                        {result.summary && (
                            <p className="mt-4 text-base leading-relaxed text-gray-700 dark:text-gray-100">
                                {result.summary}
                            </p>
                        )}
                    </div>
                </div>
            </div>

            {/* ── Распознанные вещи (состав образа) ── */}
            <CompositionSection result={result}/>

            {/* ── Три колонки ── */}
            <div className="grid grid-cols-1 lg:grid-cols-3 divide-y lg:divide-y-0 lg:divide-x divide-gray-100 dark:divide-white/10">

                {/* Колонка 1: Подоценки */}
                {ai?.scores && (
                    <div className="p-6">
                        <SectionTitle>Подоценки</SectionTitle>
                        <div className="space-y-3">
                            {SCORE_LABELS.map(([key, label]) => (
                                <ScoreBar key={key} label={label} value={ai.scores![key]}/>
                            ))}
                        </div>
                    </div>
                )}

                {/* Колонка 2: Рекомендации */}
                {ai?.recommendations && ai.recommendations.length > 0 && (
                    <div className="p-6">
                        <SectionTitle>Рекомендации</SectionTitle>
                        <div className="space-y-2.5">
                            {ai.recommendations.map((r, i) => (
                                <div key={i} className={`border-l-4 ${PRIORITY_BORDER[r.priority] ?? PRIORITY_BORDER.low} bg-gray-50 dark:bg-ink-700 rounded-r-xl p-3`}>
                                    <div className="text-sm font-semibold text-gray-900 dark:text-white">{r.target}</div>
                                    <div className="mt-0.5 text-sm text-gray-600 dark:text-gray-300 leading-snug">{r.description}</div>
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                {/* Колонка 3: Проблемы + Сценарии + Выбытие */}
                <div className="p-6 space-y-6">
                    {ai?.detectedIssues && ai.detectedIssues.length > 0 && (
                        <div>
                            <SectionTitle>Замеченные проблемы</SectionTitle>
                            <div className="space-y-3">
                                {ai.detectedIssues.map((issue, i) => (
                                    <div key={i}>
                                        <div className="flex items-center gap-2 mb-1">
                                            <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${SEVERITY_COLORS[issue.severity]}`}>
                                                {issue.severity === 'high' ? 'высокая' : issue.severity === 'medium' ? 'средняя' : 'низкая'}
                                            </span>
                                            <span className="text-sm font-semibold text-gray-900 dark:text-white">{issue.item}</span>
                                        </div>
                                        <p className="text-sm text-gray-600 dark:text-gray-300 leading-snug">{issue.description}</p>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    {ai?.scenarioAssessment && ai.scenarioAssessment.length > 0 && (
                        <div>
                            <SectionTitle>По сценариям</SectionTitle>
                            <div className="flex flex-wrap gap-1.5">
                                {ai.scenarioAssessment.map((s, i) => {
                                    const sc = s.score;
                                    const chip = sc >= 7
                                        ? 'bg-green-100 dark:bg-green-500/20 text-green-800 dark:text-green-300'
                                        : sc >= 5
                                        ? 'bg-yellow-100 dark:bg-yellow-500/20 text-yellow-800 dark:text-yellow-300'
                                        : 'bg-red-100 dark:bg-red-500/20 text-red-800 dark:text-red-300';
                                    return (
                                        <span key={i} className={`rounded-lg px-2.5 py-1 text-xs font-medium ${chip}`}>
                                            {SCENARIO_LABELS[s.scenario] ?? s.scenario} · {s.score}
                                        </span>
                                    );
                                })}
                            </div>
                        </div>
                    )}

                    {ai?.retirementCandidates && ai.retirementCandidates.length > 0 && (
                        <div>
                            <SectionTitle>Кандидаты на выбытие</SectionTitle>
                            <div className="space-y-2">
                                {ai.retirementCandidates.map((c, i) => (
                                    <div key={i}>
                                        <div className="text-sm font-semibold text-gray-900 dark:text-white">{c.description}</div>
                                        <div className="text-sm text-gray-600 dark:text-gray-300">{c.reason}</div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
        </>
    );
}

function HistoryItem({item, onClick}: {item: EvaluationResult; onClick: () => void}) {
    const v = VERDICT_LABELS[item.verdict ?? ''];
    return (
        <button onClick={onClick} className="w-full text-left bg-white dark:bg-ink-800 rounded-xl p-4 flex gap-3 hover:ring-2 hover:ring-brand-500 transition border border-gray-100 dark:border-white/10">
            {item.imageUrl && (
                <img src={item.imageUrl} alt="образ" className="w-16 h-20 object-cover rounded-lg shrink-0"/>
            )}
            <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-bold text-gray-900 dark:text-white">{item.overallScore?.toFixed(1)}/10</span>
                    {v && <span className={`text-sm font-medium ${v.color}`}>{v.label}</span>}
                </div>
                {item.summary && <p className="mt-1 text-sm text-gray-600 dark:text-gray-300 line-clamp-2">{item.summary}</p>}
                <p className="mt-1 text-xs text-gray-400">{formatDateTime(item.createdAt)}</p>
            </div>
        </button>
    );
}

const OutfitEvaluationPage: React.FC = () => {
    useDocumentTitle();
    const fileRef = useRef<HTMLInputElement>(null);
    const [preview, setPreview] = useState<string | null>(null);
    const [base64, setBase64] = useState<string | null>(null);
    const [comment, setComment] = useState('');
    const [scenario, setScenario] = useState('');
    const [weatherTemp, setWeatherTemp] = useState('');
    const [submitting, setSubmitting] = useState(false);
    const [result, setResult] = useState<EvaluationResult | null>(null);
    const [history, setHistory] = useState<EvaluationResult[] | null>(null);
    const [loadingHistory, setLoadingHistory] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const {showToast} = useToast();

    /** Единый вход для выбора файла, drag-and-drop и буфера: формат и размер проверяем до чтения. */
    const handleFile = useCallback((file: File) => {
        const reason = imageRejectReason(file);
        if (reason) {
            setError(reason);
            return;
        }
        setError(null);
        const reader = new FileReader();
        reader.onload = e => {
            const dataUrl = e.target?.result as string;
            setPreview(dataUrl);
            setBase64(dataUrl.split(',')[1]);
        };
        reader.readAsDataURL(file);
    }, []);

    const onDrop = useCallback((e: React.DragEvent) => {
        e.preventDefault();
        const file = e.dataTransfer.files[0];
        if (file) handleFile(file);
    }, [handleFile]);

    // Вставка из буфера (paste-photo-from-clipboard, FR-2): форма принимает одно фото,
    // поэтому из буфера берём первое изображение. Пока показан результат — форма скрыта,
    // и жест не должен молча подменять фото под вердиктом.
    // Пока идёт оценка, подмена фото под спиннером тоже недопустима.
    const onPasteImages = useCallback((files: File[]) => {
        handleFile(files[0]);
        if (files.length > 1) showToast(`В буфере ${files.length} изображений — взято первое`, 'info');
    }, [handleFile, showToast]);
    usePasteImages(onPasteImages, !result && !submitting);

    const submit = async () => {
        if (!base64) return;
        setSubmitting(true);
        setError(null);
        try {
            const api = await createAxiosInstance();
            const res = await api.post<EvaluationResult>('/outfit-evaluation', {
                photoBase64: base64,
                comment: comment || undefined,
                scenario: scenario || undefined,
                weatherTemp: weatherTemp ? Number(weatherTemp) : undefined,
            });
            const r = res.data;
            if (r.aiResponse && typeof r.aiResponse === 'string') {
                try { r.aiResponse = JSON.parse(r.aiResponse as unknown as string); } catch {}
            }
            setResult(r);
        } catch (err: unknown) {
            const axiosErr = err as {response?: {data?: {message?: string; error?: string}}};
            const serverMsg = axiosErr?.response?.data?.message || axiosErr?.response?.data?.error;
            setError(serverMsg || 'Ошибка при оценке образа. Попробуйте ещё раз.');
        } finally {
            setSubmitting(false);
        }
    };

    const loadHistory = useCallback(async () => {
        setLoadingHistory(true);
        try {
            const api = await createAxiosInstance();
            const res = await api.get<{content: EvaluationResult[]}>('/outfit-evaluation/history');
            const items = res.data.content ?? [];
            items.forEach(r => {
                if (r.aiResponse && typeof r.aiResponse === 'string') {
                    try { r.aiResponse = JSON.parse(r.aiResponse as unknown as string); } catch {}
                }
            });
            setHistory(items);
        } catch {
            setHistory([]);
        } finally {
            setLoadingHistory(false);
        }
    }, []);

    const reset = () => {
        setPreview(null); setBase64(null); setComment(''); setScenario('');
        setWeatherTemp(''); setResult(null); setError(null);
    };

    return (
        <div className={`mx-auto px-4 py-6 space-y-6 ${result ? 'w-full' : 'max-w-2xl'}`}>
            {!result && (
                <div className="flex items-center gap-2">
                    <SparklesIcon className="w-6 h-6 text-brand-500"/>
                    <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Оценка образа</h1>
                </div>
            )}

            {result ? (
                <ResultCard result={result} onClose={reset}/>
            ) : (
                <div className="bg-white dark:bg-ink-800 rounded-2xl shadow p-6 space-y-4 border border-gray-100 dark:border-white/10">
                    <div
                        onDrop={onDrop} onDragOver={e => e.preventDefault()}
                        onClick={() => fileRef.current?.click()}
                        className="border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-xl flex flex-col items-center justify-center cursor-pointer hover:border-brand-500 dark:hover:border-brand-400 transition min-h-[180px]"
                    >
                        {preview ? (
                            <img src={preview} alt="preview" className="max-h-64 rounded-xl object-contain"/>
                        ) : (
                            <div className="text-center p-6">
                                <SparklesIcon className="w-10 h-10 text-gray-300 dark:text-gray-600 mx-auto mb-2"/>
                                <p className="text-sm text-gray-500 dark:text-gray-400">Нажмите или перетащите фото образа</p>
                                <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">Лучше в полный рост</p>
                                <p className="hidden sm:block text-xs text-gray-400 dark:text-gray-500 mt-1">
                                    или вставьте из буфера: Ctrl/⌘+V
                                </p>
                            </div>
                        )}
                    </div>
                    <input ref={fileRef} type="file" accept={IMAGE_ACCEPT} className="hidden"
                        onChange={e => { const f = e.target.files?.[0]; if (f) handleFile(f); }}/>

                    <textarea
                        value={comment} onChange={e => setComment(e.target.value)}
                        placeholder="Куда идёте? Что хотите узнать? (необязательно)"
                        className="w-full rounded-xl border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-ink-700 text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 p-3 text-sm resize-none h-20 focus:outline-none focus:ring-2 focus:ring-brand-500"
                    />

                    <div className="flex gap-3">
                        <select value={scenario} onChange={e => setScenario(e.target.value)}
                            className="flex-1 rounded-xl border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-ink-700 text-gray-900 dark:text-white p-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500">
                            <option value="">Сценарий (необязательно)</option>
                            {Object.entries(SCENARIO_LABELS).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                        </select>
                        <input type="number" value={weatherTemp} onChange={e => setWeatherTemp(e.target.value)}
                            placeholder="°C"
                            className="w-24 rounded-xl border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-ink-700 text-gray-900 dark:text-white p-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500"/>
                    </div>

                    {error && <p className="text-sm text-red-500 dark:text-red-400">{error}</p>}

                    <button onClick={submit} disabled={!base64 || submitting}
                        className="w-full py-3 rounded-xl bg-brand-500 hover:bg-brand-600 disabled:opacity-40 text-white font-semibold transition flex items-center justify-center gap-2">
                        {submitting ? <><InlineSpinner/><span>Оцениваю…</span></> : <><SparklesIcon className="w-5 h-5"/><span>Оценить образ</span></>}
                    </button>
                </div>
            )}

            <div>
                {history === null ? (
                    <button onClick={loadHistory} className="text-sm text-brand-500 hover:underline">
                        Показать историю оценок
                    </button>
                ) : loadingHistory ? (
                    <InlineSpinner/>
                ) : history.length === 0 ? (
                    <p className="text-sm text-gray-400">История пуста</p>
                ) : (
                    <div className="space-y-3">
                        <h2 className="text-sm font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide">История</h2>
                        {history.map(item => (
                            <HistoryItem key={item.id} item={item} onClick={() => setResult(item)}/>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
};

export default OutfitEvaluationPage;
